import React, { useEffect, useState } from 'react';
import { suppliersAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Truck, ShieldCheck, TrendingUp, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { DeliveryDrilldown, QualityDrilldown } from '@/components/SupplierPerformanceDrilldown';

const pctColor = (pct) => {
  if (pct === null || pct === undefined) return 'text-slate-400';
  if (pct >= 90) return 'text-emerald-600';
  if (pct >= 75) return 'text-amber-600';
  return 'text-red-600';
};

const barColor = (pct) => {
  if (pct >= 90) return 'bg-emerald-500';
  if (pct >= 75) return 'bg-amber-500';
  return 'bg-red-500';
};

const MetricRing = ({ pct, label }) => (
  <div className="flex flex-col items-center justify-center">
    <div className={`text-3xl font-bold ${pctColor(pct)}`}>
      {pct === null || pct === undefined ? '—' : `${pct}%`}
    </div>
    <div className="text-[11px] text-slate-500 mt-0.5">{label}</div>
  </div>
);

const Bar = ({ pct }) => (
  <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
    <div className={`h-full rounded-full transition-all ${barColor(pct ?? 0)}`} style={{ width: `${pct ?? 0}%` }} />
  </div>
);

/**
 * Past-period supplier performance card with drill-downs.
 * @param {string} supplierId
 * @param {boolean} showQuality - render the quality section
 * @param {object} params - optional { months } or { date_from, date_to }
 */
export const SupplierPerformanceCard = ({ supplierId, showQuality = true, params = { months: 12 } }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expandDelivery, setExpandDelivery] = useState(false);
  const [expandQuality, setExpandQuality] = useState(false);

  const paramKey = JSON.stringify(params);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    suppliersAPI
      .performance(supplierId, params)
      .then((res) => { if (active) setData(res.data); })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [supplierId, paramKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Card className="border-indigo-200" data-testid="supplier-performance-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-indigo-600" />
          Supplier Performance
        </CardTitle>
        <CardDescription className="text-xs">
          {data
            ? `Period ${data.period_from} → ${data.period_to}`
            : 'Loading period…'}
          <span className="text-slate-400"> · click a metric to view backup details</span>
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading && <p className="text-sm text-slate-400" data-testid="perf-loading">Loading performance…</p>}
        {error && <p className="text-sm text-red-500" data-testid="perf-error">Failed to load performance data.</p>}
        {data && !loading && (
          <div className={`grid grid-cols-1 ${showQuality ? 'md:grid-cols-2' : ''} gap-4`}>
            {/* On-time delivery */}
            <div className="border rounded-lg bg-slate-50/50 overflow-hidden" data-testid="perf-delivery">
              <button
                type="button"
                onClick={() => setExpandDelivery((v) => !v)}
                className="w-full text-left p-4 hover:bg-slate-100/60 transition-colors"
                data-testid="perf-delivery-toggle"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                    {expandDelivery ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    <Truck className="w-4 h-4 text-blue-600" /> On-Time Delivery
                  </div>
                  <MetricRing pct={data.delivery.on_time_pct} label="on-time" />
                </div>
                <Bar pct={data.delivery.on_time_pct} />
                <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                  <div>
                    <div className="text-sm font-semibold text-emerald-600" data-testid="perf-delivery-ontime">{data.delivery.on_time}</div>
                    <div className="text-[10px] text-slate-500">On Time</div>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-red-600" data-testid="perf-delivery-delayed">{data.delivery.delayed}</div>
                    <div className="text-[10px] text-slate-500">Delayed</div>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-700">{data.delivery.total_evaluated}</div>
                    <div className="text-[10px] text-slate-500">POs Received</div>
                  </div>
                </div>
              </button>
              {expandDelivery && (
                <div className="border-t bg-white px-2 pb-2">
                  <DeliveryDrilldown rows={data.delivery.rows} />
                </div>
              )}
            </div>

            {/* Quality */}
            {showQuality && (
              <div className="border rounded-lg bg-slate-50/50 overflow-hidden" data-testid="perf-quality">
                <button
                  type="button"
                  onClick={() => setExpandQuality((v) => !v)}
                  className="w-full text-left p-4 hover:bg-slate-100/60 transition-colors"
                  data-testid="perf-quality-toggle"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                      {expandQuality ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      <ShieldCheck className="w-4 h-4 text-emerald-600" /> Quality (QC Acceptance)
                    </div>
                    <MetricRing pct={data.quality.quality_pct} label="accepted" />
                  </div>
                  <Bar pct={data.quality.quality_pct} />
                  <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                    <div>
                      <div className="text-sm font-semibold text-emerald-600" data-testid="perf-quality-accepted">{data.quality.accepted_qty}</div>
                      <div className="text-[10px] text-slate-500">Accepted Qty</div>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-amber-600" data-testid="perf-quality-mrb">{data.quality.mrb_qty}</div>
                      <div className="text-[10px] text-slate-500">MRB Qty</div>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-700">{data.quality.total_inspected_qty}</div>
                      <div className="text-[10px] text-slate-500">Inspected Qty</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-center gap-3 mt-2 text-[10px] text-slate-500">
                    <span>{data.quality.receipts_inspected} receipts inspected</span>
                    {data.quality.mrb_cases > 0 && (
                      <Badge variant="outline" className="text-[10px] gap-1 text-amber-700 border-amber-300">
                        <AlertTriangle className="w-3 h-3" /> {data.quality.mrb_cases} MRB case(s)
                      </Badge>
                    )}
                  </div>
                </button>
                {expandQuality && (
                  <div className="border-t bg-white px-2 pb-2">
                    <QualityDrilldown rows={data.quality.rows} />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SupplierPerformanceCard;
